import type { SortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import type { Transform } from '@dnd-kit/utilities';
import { CSS } from '@dnd-kit/utilities';

export const useOptionSortable = ({
  id,
  disabled,
}: {
  id: string;
  disabled?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: id,
    strategy: customRectSortingStrategy,
    animateLayoutChanges: () => false,
    disabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return {
    attributes,
    listeners,
    setNodeRef,
    transition,
    style,
    isDragging,
  };
};

/**
 * A custom sorting strategy for vertical lists with variable height items.
 *
 * It correctly calculates the displacement for items being moved aside by accounting
 * for the specific height of the active (dragged) item.
 *
 * Key behavior:
 * - When a TALL item drags over a SHORT item: the short item moves by the TALL item's height
 * - When a SHORT item drags over a TALL item: the tall item moves by the SHORT item's height
 * - This ensures visual displacement matches the actual space being swapped
 */
export const customRectSortingStrategy: SortingStrategy = ({
  activeIndex,
  overIndex,
  index,
  activeNodeRect,
}): Transform | null => {
  // Return null early if we don't have the required data or if there's no movement.
  // This is an optimization and prevents unnecessary calculations.
  if (!activeNodeRect || overIndex === null || activeIndex === overIndex) {
    return null;
  }

  // The active item's transform is managed by `DndContext` and `useDraggable`.
  // The strategy should not apply any transform to it.
  if (index === activeIndex) {
    return { x: 0, y: 0, scaleX: 1, scaleY: 1 };
  }

  const isDraggingDown = activeIndex < overIndex;
  const displacementY = activeNodeRect.height;

  let transform: Transform | null = null;

  if (isDraggingDown) {
    // If dragging down, items between the active and over index need to move up
    // by the height of the active item.
    if (index > activeIndex && index <= overIndex) {
      transform = { x: 0, y: -displacementY, scaleX: 1, scaleY: 1 };
    }
  } else {
    // If dragging up, items between the over and active index need to move down
    // by the height of the active item.
    if (index >= overIndex && index < activeIndex) {
      transform = { x: 0, y: displacementY, scaleX: 1, scaleY: 1 };
    }
  }

  // Items outside the affected range are not transformed.
  return transform;
};
